import { useIntl } from 'react-intl';
import styled from 'styled-components';

import profileCompletePreview from 'app/assets/leads/profile-complete-preview.png';
import profileIncompletePreview from 'app/assets/leads/profile-incomplete-preview.png';
import { ProfileStatusIcon } from 'app/features/leads/components/leads-profile-status-icon';

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(8)};
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(1)};
`;

const Eyebrow = styled.p`
  margin: 0;
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: 1rem;
  font-weight: ${({ theme: { typography } }) => typography.light};
  line-height: normal;
  color: ${({ theme }) => theme.semantic.neutrals_600};
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: 1.25rem;
  font-weight: ${({ theme: { typography } }) => typography.bold};
  line-height: normal;
  color: ${({ theme }) => theme.semantic.text_small_elements};
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme: { spacing } }) => spacing(8)};

  ${({ theme }) =>
    theme.media.medium(
      () => `
        grid-template-columns: 1fr 1fr;
        gap: ${theme.spacing(8)};
      `,
    )}
`;

const ComparisonCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(6)};
  padding: ${({ theme: { spacing } }) => `${spacing(6)} ${spacing(8)} ${spacing(8)}`};
  border-radius: ${({ theme: { spacing } }) => `0 ${spacing(2)} ${spacing(2)} 0`};
  background: ${({ theme }) => theme.semantic.neutrals_100};
`;

const IncompleteCard = styled(ComparisonCard)`
  border-left: 0.0625rem solid ${({ theme }) => theme.semantic.error_500};
`;

const CompleteCard = styled(ComparisonCard)`
  border-left: 0.0625rem solid ${({ theme }) => theme.semantic.success_500};
`;

const CardHeader = styled.div`
  display: flex;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  align-items: center;
  width: 100%;
`;

const CardLabel = styled.h3`
  margin: 0;
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: 1.25rem;
  font-weight: ${({ theme: { typography } }) => typography.bold};
  line-height: normal;
  color: ${({ theme }) => theme.semantic.neutrals_800};
`;

const PreviewFrame = styled.div`
  position: relative;
  width: 17.3125rem;
  max-width: 100%;
`;

const PreviewImage = styled.img<{ $height: string; $objectPosition?: string }>`
  display: block;
  width: 100%;
  height: ${({ $height }) => $height};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  object-fit: cover;
  object-position: ${({ $objectPosition }) => $objectPosition ?? 'top'};
`;

const PreviewGradient = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  pointer-events: none;
  background: linear-gradient(180deg, transparent 59%, ${({ theme }) => theme.semantic.neutrals_100} 93%);
`;

export const LeadsProfileComparison = () => {
  const intl = useIntl();

  return (
    <Section data-testid="leads-empty-profile-comparison">
      <SectionHeading>
        <Eyebrow>{intl.formatMessage({ id: 'leads.empty.profileIncomplete.sectionEyebrow' })}</Eyebrow>
        <SectionTitle>{intl.formatMessage({ id: 'leads.empty.profileIncomplete.sectionTitle' })}</SectionTitle>
      </SectionHeading>
      <ComparisonGrid>
        <IncompleteCard data-testid="leads-profile-comparison-incomplete">
          <CardHeader>
            <ProfileStatusIcon variant="incomplete" />
            <CardLabel>{intl.formatMessage({ id: 'leads.empty.profileIncomplete.incompleteLabel' })}</CardLabel>
          </CardHeader>
          <PreviewFrame>
            <PreviewImage src={profileIncompletePreview} alt="" $height="27.1875rem" $objectPosition="center top" />
            <PreviewGradient />
          </PreviewFrame>
        </IncompleteCard>
        <CompleteCard data-testid="leads-profile-comparison-complete">
          <CardHeader>
            <ProfileStatusIcon variant="complete" />
            <CardLabel>{intl.formatMessage({ id: 'leads.empty.profileIncomplete.completeLabel' })}</CardLabel>
          </CardHeader>
          <PreviewFrame>
            <PreviewImage src={profileCompletePreview} alt="" $height="26.4375rem" $objectPosition="center -15%" />
            <PreviewGradient />
          </PreviewFrame>
        </CompleteCard>
      </ComparisonGrid>
    </Section>
  );
};

export default LeadsProfileComparison;
